import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocalizedFormatters } from "../../../../shared/hooks/useLocalizedFormatters";

import Button from "../../../../components/essentials/button/Button";
import AcedSelect, {
	SelectOption,
	SelectOptions,
} from "../../../../components/essentials/input/select/AcedSelect";
import AcedInput from "../../../../components/essentials/input/text/AcedInput";
import { selectProfile, setProfile } from "../../../../core/auth/authSlice";
import { usePositions } from "../../../../core/customuse/usePositions";
import { ExperiencesApiRoutes } from "../../../../shared/api-routes/member/experiences-api.routes";
import { ProfileData } from "../../../../shared/models";
import http from "../../../../shared/utils/http";

import ClickIcon from "../../../../components/essentials/icon-buttons/click-icon/ClickIcon";
import DeleteButton from "../../../../components/essentials/icon-buttons/delete-button/DeleteButton";
import "./ExperienceCard.scss";

export interface ExperienceInfo {
	position: string;
	from: string;
	to: string;
	team: string;
	league: { name: string; category: string; id?: string };
	description: string;
	isNew: boolean;
	id?: string;
	setNewExp?: any;
}

export type ExperienceCardProps = {
	myExp?: boolean;
	info: ExperienceInfo;
	register: any;
	removeExperienceAction: any;
	setNewExp: any;
	isDirty: boolean;
	index: number;
	errors: any;
	sport: string;
};

const ExperienceCard: React.FC<ExperienceCardProps> = ({
	info,
	index,
	removeExperienceAction,
	setNewExp,
	myExp,
	sport,
}) => {
	const [isEdit, setIsEdit] = useState(info.isNew || false);
	const [data, setData] = useState(info);
	const [isNew, setIsNew] = useState(info.isNew || false);
	const formRef = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { roles } = usePositions("volleyball");
	const { formatDate } = useLocalizedFormatters();

	// Auto-scroll to form when editing a new experience
	useEffect(() => {
		if (isEdit && isNew && formRef.current) {
			// Small delay to ensure the form is rendered
			setTimeout(() => {
				formRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "start",
					inline: "nearest",
				});
			}, 100);
		}
	}, [isEdit, isNew]);

	// Helper function to format date as "Mon Year" using localized formatting
	const formatDateDisplay = (dateString?: string) => {
		if (!dateString) return "";
		try {
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return dateString; // fallback to original string
			return formatDate(date, { format: "medium" });
		} catch {
			return dateString; // fallback to original string
		}
	};

	const {
		control,
		handleSubmit,
		formState: { errors, isDirty },
		watch,
		setValue,
	} = useForm({
		mode: "onChange",
		values: {
			position: info.position,
			team: info.team,
			league: info.league,
			description: info.description,
			from: info.from,
			to: info.to,
			category: "",
		},
	});

	const { experiences } = useSelector(selectProfile) as ProfileData;

	const onExperiencesCancel = () => {
		setIsEdit(false);
		if (isNew) {
			removeExperienceAction(index);
		}
	};

	const onExperiencesSubmit = (formData: any) => {
		const body = {
			position: formData.position,
			team: formData.team,
			league: {
				name: formData.league,
				category: formData.category,
			},
			description: formData.description,
			to: formData.to,
			from: formData.from,
		};
		if (isNew) {
			http
				.post(ExperiencesApiRoutes.CREATE_EXPERIENCE("member"), body)
				.then((response) => {
					setIsEdit(false);
					let _data = { ...response, isNew: false };
					setData({ ...response, isNew: false });

					setIsNew(false);
					if (experiences)
						dispatch(setProfile({ experiences: [...experiences, response] }));
					setNewExp(false);
				});
		} else {
			http
				.patch(`/members/member/experiences/${data.id}`, body)
				.then((response) => {
					setIsEdit(false);
					let _data = { ...response };
					setData(_data);

					dispatch(
						setProfile({
							experiences: experiences?.map((exp) =>
								exp.id === data.id
									? {
											...exp,
											position: formData.position,
											team: response.team,
											league: response.league,
											description: response.description,
											to: response.to,
											from: response.from,
										}
									: exp,
							),
						}),
					);
				});
		}
	};

	const [categories, setCategories] = useState<SelectOption[]>([]);
	const [leagues, setLeagues] = useState<SelectOptions>([]);
	const [leagueData, setLeagueData] = useState<{
		name: string;
		category: string;
	}>({
		name: "",
		category: "",
	});

	const watchCategory = watch("category");

	useEffect(() => {
		if (!sport) {
			setCategories([]);
			return;
		}

		const sportLabel = t("sportData.sportsLabels." + sport);
		setCategories([
			{ value: "M", label: `Male ${sportLabel}` },
			{ value: "F", label: `Female ${sportLabel}` },
		]);
	}, []);

	useEffect(() => {
		if (!watchCategory) {
			setLeagues([]);
			return;
		}
		http.get(`/utils/leagues?category=${watchCategory}`).then((response) => {
			const leagueOptions: SelectOption[] = response.map(
				(value: { name: string; category: string }) =>
					({
						value: value.name,
						label: value.name,
					}) as SelectOption,
			);
			setLeagues(leagueOptions);
		});
	}, [watchCategory]);

	useEffect(() => {
		// League should always be an object with name and category
		if (typeof data.league === "object" && data.league !== null) {
			setLeagueData(data.league);
			setValue("category", data.league.category);
			setValue("league", data.league);
		}
	}, []);

	return (
		<>
			{!isEdit ? (
				<div className="experience-card">
					<div className="experience-card__header">
						<div className="experience-card__title-section">
							<h4 className="experience-card__position">
								{t("sportData.positions.volleyball." + data.position) ||
									data.position}
							</h4>
							<p className="experience-card__team-info">
								{data.team}
								{leagueData.name &&
									leagueData.category &&
									` • ${leagueData.name} (${leagueData.category})`}
							</p>
							{(data.from || data.to) && (
								<p className="experience-card__period">
									{data.from && data.to
										? `${formatDateDisplay(data.from)} - ${formatDateDisplay(
												data.to,
											)}`
										: data.from
											? `${formatDateDisplay(data.from)} - Present`
											: formatDateDisplay(data.to)}
								</p>
							)}
						</div>
						{myExp && (
							<div className="experience-card__actions">
								<ClickIcon
									icon="edit"
									onClick={() => setIsEdit(!isEdit)}
									tooltip={t("common.buttons.edit")}
									size="s"
								/>
								<DeleteButton
									onClick={() => removeExperienceAction(index, data.id)}
									tooltip={t("common.buttons.delete")}
									//size="s"
								/>
							</div>
						)}
					</div>

					<div className="experience-card__content">
						{data.description && (
							<p className="experience-card__description">{data.description}</p>
						)}
					</div>
				</div>
			) : (
				<div
					ref={formRef}
					className="experience-card experience-card--editing"
				>
					<h4 className="experience-card__edit-title">
						{isNew ? "Add New Experience" : "Edit Experience"}
					</h4>

					<div className="experience-card__form-grid">
						{/* Basic Information */}
						<div className="experience-card__form-section">
							<h5 className="experience-card__section-title">
								{t("common.words.position")} & {t("createJob.teamLabel")}
							</h5>

							<div className="experience-card__input-field">
								<AcedSelect
									control={control}
									options={roles}
									placeholder={t("common.words.position")}
									name="position"
									className="experience-card__input"
								/>
							</div>

							<div className="experience-card__input-field">
								<AcedInput
									placeholder={t("createJob.teamLabel")}
									control={control}
									name="team"
									className="experience-card__input"
									rules={{
										required: t("createJob.fieldRequired"),
									}}
								/>
							</div>

							<div className="experience-card__input-field">
								<AcedSelect
									placeholder={t("createJob.categoryLabel")}
									control={control}
									name="category"
									options={categories}
									isDisabled={!sport}
									rules={{
										required: t("createJob.fieldRequired"),
									}}
								/>
							</div>

							<div className="experience-card__input-field">
								<AcedSelect
									control={control}
									options={leagues}
									placeholder={t("common.words.league")}
									name="league"
									isDisabled={!watchCategory}
									rules={{ required: t("createJob.fieldRequired") }}
								/>
							</div>
						</div>

						{/* Time Period */}
						<div className="experience-card__form-section">
							<h5 className="experience-card__section-title">
								{t("common.words.season")}
							</h5>

							<div className="experience-card__date-row">
								<div className="experience-card__input-field">
									<AcedInput
										placeholder="From"
										type="date"
										control={control}
										name="from"
										className="experience-card__input"
										rules={{
											required: t("createJob.fieldRequired"),
										}}
									/>
								</div>

								<div className="experience-card__input-field">
									<AcedInput
										placeholder="To"
										type="date"
										control={control}
										name="to"
										className="experience-card__input"
										rules={{
											required: t("createJob.fieldRequired"),
										}}
									/>
								</div>
							</div>
						</div>

						{/* Description */}
						<div className="experience-card__form-section">
							<h5 className="experience-card__section-title">
								{" "}
								{t("createJob.review.description")}
							</h5>

							<div className="experience-card__input-field">
								<AcedInput
									placeholder={t("createJob.review.description")}
									control={control}
									name="description"
									className="experience-card__input"
									rules={{ required: t("createJob.fieldRequired") }}
								/>
							</div>
						</div>
					</div>

					<div className="d-flex center experience-card__button-row">
						<Button
							className="btn btn-primary"
							type="submit"
							action={() => onExperiencesCancel()}
						>
							{t("profile.profilePage.cancelBtnLabel")}
						</Button>

						<Button
							className="btn btn-primary"
							type="submit"
							action={handleSubmit(onExperiencesSubmit)}
						>
							{t("common.buttons.save")}
						</Button>
					</div>
				</div>
			)}
		</>
	);
};

export default ExperienceCard;
